import { LABEL_PAGE, sheetCss } from './labelSheet';

/** Build one PDF with each parcel's intact carrier PDF or browser-drawn sticker followed by its PACKING sheets. */

/** Dots per inch a drawing is made at: three times a 203dpi head and twice a 300dpi one, so a 0.375mm bar stays whole on either. */
export const SHEET_DPI = 600;

const PT_PER_MM = 72 / 25.4;

const CSS_PX_PER_MM = 96 / 25.4;

const XHTML_NS = 'http://www.w3.org/1999/xhtml';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The class a drawing's rules are confined to, so nothing else is styled by them. */
export const RASTER_SCOPE = 'packing-sheet-raster';

const CANNOT_DRAW = 'This browser cannot draw the sheet as an image.';

/** One page of html to draw: its markup, the rules it needs, and the typefaces to inline when it asks for any. */
export interface Drawing {
  html: string;
  css?: string;
  fonts?: () => Promise<string>;
}

/** A drawing as the bytes of a png, SHEET_DPI on the sticker's size. */
export type HtmlRasterizer = (drawing: Drawing) => Promise<Uint8Array>;

const bytesOf = (blob: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The document could not be read.'));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(blob);
  });

const base64Of = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The typeface could not be read.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(blob);
  });

/**
 * The drawing as well-formed XHTML carrying its own rules — what an svg foreignObject takes. The html is parsed
 * by the browser and serialized back as XML, so a `<col>` a sheet writes open is closed the way XML needs.
 */
const xhtmlOf = (html: string, css: string): string => {
  const scope = document.createElementNS(XHTML_NS, 'div');
  scope.setAttribute('class', RASTER_SCOPE);
  scope.innerHTML = html;
  const style = document.createElementNS(XHTML_NS, 'style');
  style.textContent = css;
  scope.prepend(style);
  return new XMLSerializer().serializeToString(scope);
};

/**
 * Draws one page the way the browser lays it out into a png the size of the sticker at SHEET_DPI. The drawing
 * goes through an svg image, which is the one way a browser paints html onto a canvas without a library; a
 * browser that cannot decode one says so.
 */
/** CSS pixels of strip below the sticker that carries the typeface probe; drawn, read, never kept. */
const PROBE_CSS = 24;

/** How long a drawing waits for its inlined typefaces; past it the sticker is refused, not printed with blank text. */
const FACE_WAIT_MS = 4000;

const FACE_POLL_MS = 100;

/** Said on the admin when a sticker's typefaces never came in: a label missing its tracking number must not reach a parcel. */
export const FACES_LATE =
  'The label typefaces did not load in time, so the sticker was not printed. Press Print again.';

/**
 * One cell per face the waybill uses — Latin and Hangul, regular and bold. A face still loading draws its
 * text invisibly (the font-display block period), so the drawing is only taken once every cell shows ink.
 */
const faceProbe = (top: number, width: number): string =>
  `<div style="position:absolute;left:0;top:${top}px;width:${width}px;height:${PROBE_CSS}px;display:flex;font-family:'Noto Sans KR';font-size:16px;line-height:${PROBE_CSS}px;color:#000;background:#fff">` +
  ['400', '700', '400', '700']
    .map((weight, index) => `<span style="flex:1;font-weight:${weight}">${index < 2 ? 'Ag' : '가'}</span>`)
    .join('') +
  '</div>';

const pause = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/** Whether every probe cell has ink: each face the waybill uses has come in (or fallen back). */
const facesInked = (image: HTMLImageElement, top: number, width: number, height: number): boolean => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    return true;
  }
  context.fillStyle = '#fff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, top, width, height, 0, 0, width, height);
  const cells = [0, 0, 0, 0];
  const data = context.getImageData(0, 0, width, height).data;
  for (let index = 0; index < data.length; index += 4) {
    if (data[index]! < 128) {
      const x = (index / 4) % width;
      cells[Math.min(3, Math.floor((x / width) * 4))]! += 1;
    }
  }
  canvas.width = 0;
  canvas.height = 0;
  return cells.every((ink) => ink > 0);
};

/** Whether every face came in within FACE_WAIT_MS, looking again every FACE_POLL_MS. */
const facesCameIn = async (image: HTMLImageElement, top: number, width: number, height: number): Promise<boolean> => {
  for (let waited = 0; ; waited += FACE_POLL_MS) {
    if (facesInked(image, top, width, height)) {
      return true;
    }
    if (waited >= FACE_WAIT_MS) {
      return false;
    }
    await pause(FACE_POLL_MS);
  }
};

/**
 * Draws one page the way the browser lays it out into a png the size of the sticker at SHEET_DPI. The drawing
 * goes through an svg image, which is the one way a browser paints html onto a canvas without a library; a
 * browser that cannot decode one says so. A drawing that carries its own typefaces is taken only once they
 * have come in: drawn straight after decoding, text in a face still loading comes out blank (10/13 dev), so a
 * drawing whose faces are still out after FACE_WAIT_MS is refused rather than printed half empty.
 */
export const rasterizeHtml: HtmlRasterizer = async ({ html, css = '', fonts }) => {
  const image = new Image();
  if (typeof image.decode !== 'function') {
    throw new Error(CANNOT_DRAW);
  }
  const faces = fonts ? await fonts() : '';
  const widthCss = LABEL_PAGE.widthMm * CSS_PX_PER_MM;
  const heightCss = LABEL_PAGE.heightMm * CSS_PX_PER_MM;
  const width = Math.round((LABEL_PAGE.widthMm / 25.4) * SHEET_DPI);
  const height = Math.round((LABEL_PAGE.heightMm / 25.4) * SHEET_DPI);
  const probeCss = faces ? PROBE_CSS : 0;
  const probeHeight = Math.round((probeCss / heightCss) * height);
  const body = faces ? html + faceProbe(heightCss, widthCss) : html;
  const rules = faces ? `${faces}\n.${RASTER_SCOPE} { position: relative; }\n${css}` : css;
  const svg =
    `<svg xmlns="${SVG_NS}" width="${width}" height="${height + probeHeight}" viewBox="0 0 ${widthCss} ${heightCss + probeCss}">` +
    `<foreignObject x="0" y="0" width="${widthCss}" height="${heightCss + probeCss}">${xhtmlOf(body, rules)}</foreignObject></svg>`;
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  try {
    await image.decode();
  } catch {
    throw new Error(CANNOT_DRAW);
  }
  if (faces && !(await facesCameIn(image, height, width, probeHeight))) {
    throw new Error(FACES_LATE);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error(CANNOT_DRAW);
  }
  // The sticker is white paper; the svg paints only what the page draws. The probe strip below it is left out.
  context.fillStyle = '#fff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height, 0, 0, width, height);
  const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  // The canvas's backing store is released now rather than when the collector gets to it: the next page is drawn right after.
  canvas.width = 0;
  canvas.height = 0;
  if (!png) {
    throw new Error('The page image could not be encoded.');
  }
  return bytesOf(png);
};

/** A PACKING sheet as a drawing: the sheet's own rules, confined to the drawing. */
export const packingDrawing = (sheet: string): Drawing => ({ html: sheet, css: sheetCss(`.${RASTER_SCOPE}`) });

/** The Hangul blocks, so the Korean face answers for them and the Latin face for everything else. */
const KOREAN_RANGE = 'U+1100-11FF, U+3000-303F, U+3130-318F, U+3200-32FF, U+A960-A97F, U+AC00-D7FF, U+FF00-FFEF';

/** The waybill's typeface, by weight and subset; the Latin faces come first so the Korean ones, listed last, win the Hangul. */
const WAYBILL_FACES: ReadonlyArray<readonly [weight: '400' | '700', subset: 'latin' | 'korean']> = [
  ['400', 'latin'],
  ['700', 'latin'],
  ['400', 'korean'],
  ['700', 'korean'],
];

let waybillFacesOnce: Promise<string> | undefined;

const loadWaybillFaces = async (): Promise<string> => {
  const rules = await Promise.all(
    WAYBILL_FACES.map(async ([weight, subset]) => {
      const response = await fetch(`/fonts/noto-sans-kr-${subset}-${weight}-normal.woff2`);
      if (!response.ok) {
        throw new Error(`The waybill typeface could not be read (${response.status}).`);
      }
      const data = await base64Of(await response.blob());
      const range = subset === 'korean' ? `unicode-range: ${KOREAN_RANGE}; ` : '';
      return `@font-face { font-family: 'Noto Sans KR'; font-style: normal; font-weight: ${weight}; ${range}src: url(data:font/woff2;base64,${data}) format('woff2'); }`;
    }),
  );
  return rules.join('\n');
};

/**
 * Noto Sans KR, the waybill's typeface, as @font-face rules with the files inlined: a drawing made through an
 * svg image reaches no font the page loaded, only what the drawing itself carries. Read once per page load.
 */
export const waybillFaces = (): Promise<string> =>
  (waybillFacesOnce ??= loadWaybillFaces().catch((cause: unknown) => {
    waybillFacesOnce = undefined;
    throw cause;
  }));

/** The Admin-drawn Deleo-family sticker, as the hidden sheet rendered it in its own typeface. */
export const waybillDrawing = (html: string): Drawing => ({ html, fonts: waybillFaces });

/** One parcel's share of the document: the carrier's pdf or the pages drawn for its sticker, then its PACKING sheets. */
export interface PaperworkPart {
  orderId: number;
  pdf?: Blob;
  pages: Drawing[];
  sheets: Drawing[];
}

/** How one part fared: how many label and sheet pages went in, and why the label or the sheets did not. */
export interface PartOutcome {
  labelPages: number;
  sheetPages: number;
  /** The label could not be laid in, so the parcel is left out of the document whole. */
  failure?: string;
  /** The label is in; its PACKING sheets are not, and this is why. */
  sheetFailure?: string;
}

export interface PaperworkPdf {
  /** Null when no parcel's label could be laid in. */
  pdf: Blob | null;
  parts: PartOutcome[];
}

export type PaperworkBuilder = (parts: PaperworkPart[]) => Promise<PaperworkPdf>;

const messageOf = (cause: unknown): string => (cause instanceof Error && cause.message ? cause.message : String(cause));

/**
 * Builds the click's document, one parcel after another — a page drawn at SHEET_DPI holds a canvas of some
 * 34 MB while it is encoded, and a bulk of fifty drawn at once would hold fifty of them. A parcel whose
 * label cannot be laid in is left out whole, so no PACKING sheet travels without its sticker; a parcel
 * whose sheets cannot be drawn keeps its label and says so.
 */
export const buildPaperworkPdf = async (
  parts: PaperworkPart[],
  rasterize: HtmlRasterizer = rasterizeHtml,
): Promise<PaperworkPdf> => {
  const { PDFDocument } = await import('pdf-lib');
  const paperwork = await PDFDocument.create();
  const rollBackTo = (count: number) => {
    while (paperwork.getPageCount() > count) {
      paperwork.removePage(paperwork.getPageCount() - 1);
    }
  };
  const lay = async (drawing: Drawing) => {
    const image = await paperwork.embedPng(await rasterize(drawing));
    const page = paperwork.addPage([LABEL_PAGE.widthMm * PT_PER_MM, LABEL_PAGE.heightMm * PT_PER_MM]);
    page.drawImage(image, { x: 0, y: 0, width: page.getWidth(), height: page.getHeight() });
  };
  const outcomes: PartOutcome[] = [];
  for (const part of parts) {
    const outcome: PartOutcome = { labelPages: 0, sheetPages: 0 };
    const before = paperwork.getPageCount();
    try {
      if (part.pdf) {
        const label = await PDFDocument.load(await bytesOf(part.pdf), { ignoreEncryption: true });
        for (const page of await paperwork.copyPages(label, label.getPageIndices())) {
          paperwork.addPage(page);
          outcome.labelPages += 1;
        }
      }
      for (const drawing of part.pages) {
        await lay(drawing);
        outcome.labelPages += 1;
      }
      if (outcome.labelPages === 0) {
        throw new Error('The parcel has no label page to print.');
      }
    } catch (cause) {
      rollBackTo(before);
      outcomes.push({
        labelPages: 0,
        sheetPages: 0,
        failure: `The carrier label could not be laid in the document — ${messageOf(cause)}`,
      });
      continue;
    }
    const sheetsFrom = paperwork.getPageCount();
    try {
      for (const drawing of part.sheets) {
        await lay(drawing);
        outcome.sheetPages += 1;
      }
    } catch (cause) {
      rollBackTo(sheetsFrom);
      outcome.sheetPages = 0;
      outcome.sheetFailure = `The PACKING sheet could not be drawn — ${messageOf(cause)}`;
    }
    outcomes.push(outcome);
  }
  const pdf = paperwork.getPageCount() > 0 ? new Blob([await paperwork.save()], { type: 'application/pdf' }) : null;
  return { pdf, parts: outcomes };
};
